/**
 * Venta (Sell Out) — desempeño de la venta al consumidor final reportada por las
 * cadenas retail (tabla cantabria_labs_prod.sell_in_sell_out en Athena).
 *
 *   Resumen · Cadenas · Producto · Tiendas
 *
 * Backend: routes/sellout.py · Servicio: services/selloutService.js
 *
 * El periodo es MENSUAL (YYYYMM) porque el dato lo es: una fila por cadena,
 * sucursal, SKU y mes. La comparativa de todos los deltas es contra el MISMO
 * periodo del año anterior, no contra el periodo inmediato anterior: la categoria
 * es estacional (Heliocare son solares y concentra marzo-julio), asi que un
 * mes-contra-mes mezcla estacionalidad con desempeño y se lee al reves.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import {
  FaShoppingBasket, FaSyncAlt, FaMoneyBillWave, FaBoxes, FaStore, FaTag,
  FaExclamationTriangle, FaCalendarAlt, FaChartLine, FaChartBar, FaLayerGroup,
  FaFilter, FaTrophy, FaBullseye, FaInfoCircle, FaCubes,
} from 'react-icons/fa';
import {
  getSelloutBounds, getSelloutOverview, getSelloutChains,
  getSelloutProducts, getSelloutStores,
} from '../../services/selloutService';
import './SelloutPage.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Tooltip, Legend, Filler);

// ── Paleta categorica ────────────────────────────────────────────────────────
// Los slots se asignan en ORDEN FIJO y por ENTIDAD (cadena/marca), nunca por su
// posicion en el ranking: si el filtro cambia el orden, cada cadena conserva su
// color entre pestañas y entre recargas. Validada para daltonismo sobre fondo
// claro (peor par adyacente ΔE 9.1 protan / 19.6 vision normal).
// El navy y el naranja de marca (#073C5C / #F68D2E) se quedan en la interfaz —
// titulos, acentos de KPI — y no entran como color de serie: el navy lee como
// gris en una barra delgada y el par con el naranja no separa bien.
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300',
  '#4a3aa7', '#e34948'];
const INK = '#073C5C';
const GRID = '#eef2f6';
const AXIS = '#6b7a90';

/** Color estable por entidad: mismo nombre → mismo slot, sin importar el orden. */
const colorOf = (key, catalogo) => {
  const i = (catalogo || []).indexOf(key);
  return SERIES[(i >= 0 ? i : 0) % SERIES.length];
};

// ── Formateadores ────────────────────────────────────────────────────────────
const numFmt = new Intl.NumberFormat('es-MX');
const money = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(Number(n) || 0);
const moneyC = (n) => '$' + new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(n) || 0);
const num = (n) => numFmt.format(Number(n) || 0);
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MESES_LARGO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
  'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
/** '202607' → 'jul 26' */
const fmtYm = (ym) => (ym && ym.length === 6 ? `${MESES[Number(ym.slice(4)) - 1] || '?'} ${ym.slice(2, 4)}` : '—');
/** '202607' → 'julio 2026' */
const fmtYmLargo = (ym) => (ym && ym.length === 6 ? `${MESES_LARGO[Number(ym.slice(4)) - 1] || '?'} ${ym.slice(0, 4)}` : '—');
/** Nombre propio: el origen manda las dimensiones en MAYUSCULAS. */
const titulo = (s) => (s || '').toLowerCase().replace(/(^|[\s/|-])([a-záéíóúñ])/g, (m, a, b) => a + b.toUpperCase());

// ── Aritmetica de periodos YYYYMM ────────────────────────────────────────────
const ymAdd = (ym, n) => {
  const t = Number(ym.slice(0, 4)) * 12 + (Number(ym.slice(4)) - 1) + n;
  return `${String(Math.floor(t / 12)).padStart(4, '0')}${String((t % 12) + 1).padStart(2, '0')}`;
};
const ymList = (from, to) => {
  const out = [];
  let c = from;
  while (c <= to && out.length < 240) { out.push(c); c = ymAdd(c, 1); }
  return out;
};

/** Presets anclados al ULTIMO mes con dato, no a la fecha de hoy: el feed llega
 *  con semanas de retraso y "ultimos 3 meses" contra el calendario devolveria
 *  meses vacios que se leen como una caida de la venta. */
function buildPresets(bounds) {
  if (!bounds?.max_ym) return [];
  const max = bounds.max_ym;
  const min = bounds.min_ym || max;
  const clamp = (ym) => (ym < min ? min : ym);
  const r = (n) => ({ from: clamp(ymAdd(max, -(n - 1))), to: max });
  return [
    { key: '3m', label: '3 meses', range: r(3) },
    { key: '6m', label: '6 meses', range: r(6) },
    { key: '12m', label: '12 meses', range: r(12) },
    { key: 'ytd', label: max.slice(0, 4), range: { from: clamp(`${max.slice(0, 4)}01`), to: max } },
    { key: 'all', label: 'Todo', range: { from: min, to: max } },
  ];
}

const TABS = [
  { key: 'resumen', label: 'Resumen', icon: <FaChartLine /> },
  { key: 'cadenas', label: 'Cadenas', icon: <FaStore /> },
  { key: 'productos', label: 'Producto', icon: <FaTag /> },
  { key: 'tiendas', label: 'Tiendas', icon: <FaTrophy /> },
];

// ── Opciones base de Chart.js ────────────────────────────────────────────────
// Grid y ejes recesivos, sin numero sobre cada punto: el valor exacto vive en el
// tooltip y en la tabla que acompaña a cada grafica.
const baseOpts = (fmt = moneyC) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#fff', titleColor: INK, bodyColor: '#1e293b',
      borderColor: '#e8edf3', borderWidth: 1, padding: 10, displayColors: true,
      titleFont: { weight: '700' }, cornerRadius: 8,
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { color: AXIS, font: { size: 11 } } },
    y: {
      beginAtZero: true,
      grid: { color: GRID, drawBorder: false },
      ticks: { color: AXIS, font: { size: 11 }, callback: (v) => fmt(v) },
    },
  },
});

// ═══════════════════════════════════════════════════════════════════════════
export default function SelloutPage() {
  const [bounds, setBounds] = useState(null);
  const [catalogos, setCatalogos] = useState({ clientes: [], marcas: [] });
  const [range, setRange] = useState(null);
  const [filtros, setFiltros] = useState({ cliente: '', marca: '' });
  const [activeTab, setActiveTab] = useState('resumen');
  const [booting, setBooting] = useState(true);
  const [bootError, setBootError] = useState(null);

  const [cache, setCache] = useState({});     // { tab: data }
  const [loading, setLoading] = useState({}); // { tab: bool }
  const [errors, setErrors] = useState({});   // { tab: msg }

  const presets = useMemo(() => buildPresets(bounds), [bounds]);
  const activePreset = useMemo(
    () => presets.find((p) => range && p.range.from === range.from && p.range.to === range.to)?.key || 'custom',
    [presets, range],
  );

  // Arranque: periodo disponible + catalogos → rango por defecto (12 meses)
  useEffect(() => {
    (async () => {
      try {
        const b = await getSelloutBounds();
        setBounds(b.bounds);
        setCatalogos({ clientes: b.clientes || [], marcas: b.marcas || [] });
        const ps = buildPresets(b.bounds);
        // Arranca en el año en curso y no en "12 meses": con una serie que empieza
        // en enero de 2025, doce meses hacia atras dejan la ventana de comparacion
        // a medias y el tablero abre con todos los deltas en "—". El año en curso
        // casi siempre tiene su equivalente completo del año anterior.
        setRange(ps.find((p) => p.key === 'ytd')?.range || ps[ps.length - 1]?.range || null);
      } catch (e) {
        setBootError(e.message || 'No se pudo inicializar el modulo de Venta');
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  const fetchTab = useCallback((tab, rng, f) => {
    switch (tab) {
      case 'resumen': return getSelloutOverview(rng, f);
      case 'cadenas': return getSelloutChains(rng, f);
      case 'productos': return getSelloutProducts(rng, f, 20);
      case 'tiendas': return getSelloutStores(rng, f, 15);
      default: return Promise.resolve(null);
    }
  }, []);

  // La clave de peticion (rango + filtros) se compara contra el estado ACTUAL al
  // resolver: sin eso, una respuesta lenta de un filtro ya descartado pisa la
  // pantalla con datos de otra cadena y el usuario no tiene forma de notarlo.
  const reqKey = (rng, f, tab) => `${tab}|${rng?.from}|${rng?.to}|${f.cliente}|${f.marca}`;

  const loadTab = useCallback(async (tab, rng, f) => {
    const key = reqKey(rng, f, tab);
    setLoading((s) => ({ ...s, [tab]: true }));
    setErrors((s) => ({ ...s, [tab]: null }));
    try {
      const data = await fetchTab(tab, rng, f);
      setCache((s) => ({ ...s, [tab]: { key, data } }));
    } catch (e) {
      setErrors((s) => ({ ...s, [tab]: e.message || 'Error al cargar' }));
    } finally {
      setLoading((s) => ({ ...s, [tab]: false }));
    }
  }, [fetchTab]);

  // Cambio de rango o de filtros → invalida caché y recarga la pestaña activa
  useEffect(() => {
    if (!range) return;
    setCache({});
    setErrors({});
    loadTab(activeTab, range, filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, filtros]);

  const onTab = (tab) => {
    setActiveTab(tab);
    if (range && !cache[tab] && !loading[tab]) loadTab(tab, range, filtros);
  };

  // ── Estados globales ────────────────────────────────────────────────────────
  if (booting) {
    return <main className="so"><div className="so-state"><div className="so-spinner" /><p>Cargando módulo de Venta…</p></div></main>;
  }
  if (bootError) {
    return (
      <main className="so"><div className="so-state">
        <FaExclamationTriangle className="so-state-icon" />
        <h3>No se pudo iniciar el módulo</h3><p>{bootError}</p>
        <button className="so-btn primary" onClick={() => window.location.reload()}><FaSyncAlt /> Reintentar</button>
      </div></main>
    );
  }

  const entry = cache[activeTab];
  const data = entry?.data;
  const stale = entry && entry.key !== reqKey(range, filtros, activeTab);
  const isLoading = !!loading[activeTab];
  const err = errors[activeTab];
  const ctx = { clientes: catalogos.clientes, marcas: catalogos.marcas };

  return (
    <main className="so">
      {/* Header */}
      <header className="so-header">
        <div className="so-header-left">
          <span className="so-title-icon"><FaShoppingBasket /></span>
          <div>
            <h1 className="so-title">Venta · Sell Out</h1>
            <div className="so-subtitle">
              Venta al consumidor reportada por cadena ·{' '}
              {range ? `${fmtYmLargo(range.from)} – ${fmtYmLargo(range.to)}` : '—'}
            </div>
          </div>
        </div>
        <div className="so-header-actions">
          <button className="so-btn" onClick={() => range && loadTab(activeTab, range, filtros)} disabled={isLoading}>
            <FaSyncAlt className={isLoading ? 'so-spin' : ''} /> Actualizar
          </button>
        </div>
      </header>

      {/* Filtros */}
      <Filtros
        presets={presets} activePreset={activePreset} range={range} bounds={bounds}
        onRange={(r) => r?.from && r?.to && setRange(r)}
        catalogos={catalogos} filtros={filtros} onFiltros={setFiltros}
      />

      {/* Tabs */}
      <nav className="so-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`so-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => onTab(t.key)}>
            <span className="so-tab-icon">{t.icon}</span>{t.label}
          </button>
        ))}
      </nav>

      {/* Contenido */}
      <section className="so-content">
        {isLoading && <div className="so-state small"><div className="so-spinner" /><p>Consultando Athena…</p></div>}
        {!isLoading && err && (
          <div className="so-state small">
            <FaExclamationTriangle className="so-state-icon" />
            <p>{err}</p>
            <button className="so-btn" onClick={() => loadTab(activeTab, range, filtros)}><FaSyncAlt /> Reintentar</button>
          </div>
        )}
        {!isLoading && !err && data && !stale && (
          <>
            {activeTab === 'resumen' && <ResumenTab d={data} ctx={ctx} />}
            {activeTab === 'cadenas' && <CadenasTab d={data} ctx={ctx} />}
            {activeTab === 'productos' && <ProductosTab d={data} ctx={ctx} />}
            {activeTab === 'tiendas' && <TiendasTab d={data} />}
          </>
        )}
      </section>
    </main>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Filtros: periodo (presets + desde/hasta) · cadena · marca
// ═══════════════════════════════════════════════════════════════════════════
function Filtros({ presets, activePreset, range, bounds, onRange, catalogos, filtros, onFiltros }) {
  const meses = useMemo(
    () => (bounds?.min_ym && bounds?.max_ym ? ymList(bounds.min_ym, bounds.max_ym) : []),
    [bounds],
  );
  const activos = (filtros.cliente ? 1 : 0) + (filtros.marca ? 1 : 0);

  return (
    <div className="so-filter">
      <div className="so-filter-row">
        <span className="so-filter-label"><FaCalendarAlt /> Periodo</span>
        <div className="so-presets">
          {presets.map((p) => (
            <button key={p.key} className={`so-chip ${activePreset === p.key ? 'active' : ''}`}
              onClick={() => onRange(p.range)}>{p.label}</button>
          ))}
        </div>
        <div className="so-range">
          <select className="so-select" value={range?.from || ''}
            onChange={(e) => onRange({ from: e.target.value, to: range?.to < e.target.value ? e.target.value : range?.to })}>
            {meses.map((m) => <option key={m} value={m}>{fmtYm(m)}</option>)}
          </select>
          <span className="so-range-sep">a</span>
          <select className="so-select" value={range?.to || ''}
            onChange={(e) => onRange({ from: range?.from > e.target.value ? e.target.value : range?.from, to: e.target.value })}>
            {meses.map((m) => <option key={m} value={m}>{fmtYm(m)}</option>)}
          </select>
        </div>
      </div>

      <div className="so-filter-row">
        <span className="so-filter-label"><FaFilter /> Filtros</span>
        <select className="so-select wide" value={filtros.cliente}
          onChange={(e) => onFiltros({ ...filtros, cliente: e.target.value })}>
          <option value="">Todas las cadenas</option>
          {catalogos.clientes.map((c) => <option key={c} value={c}>{titulo(c)}</option>)}
        </select>
        <select className="so-select wide" value={filtros.marca}
          onChange={(e) => onFiltros({ ...filtros, marca: e.target.value })}>
          <option value="">Todas las marcas</option>
          {catalogos.marcas.map((m) => <option key={m} value={m}>{titulo(m)}</option>)}
        </select>
        {activos > 0 && (
          <button className="so-chip clear" onClick={() => onFiltros({ cliente: '', marca: '' })}>
            Limpiar {activos} filtro{activos > 1 ? 's' : ''}
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Piezas reutilizables
// ═══════════════════════════════════════════════════════════════════════════
function Delta({ v, invert = false }) {
  if (v == null) return <span className="so-delta none">— vs año ant.</span>;
  const up = v >= 0;
  const good = invert ? !up : up;
  return (
    <span className={`so-delta ${good ? 'up' : 'down'}`}>
      {up ? '▲' : '▼'} {Math.abs(v).toFixed(1)}% <span className="so-delta-ref">vs año ant.</span>
    </span>
  );
}

function Kpi({ label, value, hint, delta, icon, color = INK, invert }) {
  return (
    <div className="so-kpi" style={{ '--accent': color }}>
      <div className="so-kpi-head">
        <span className="so-kpi-label">{label}</span>
        <span className="so-kpi-icon">{icon}</span>
      </div>
      <div className="so-kpi-value">{value}</div>
      <div className="so-kpi-foot">
        {delta !== undefined ? <Delta v={delta} invert={invert} /> : null}
        {hint && <span className="so-kpi-hint">{hint}</span>}
      </div>
    </div>
  );
}

function Card({ title, icon, children, className = '', note }) {
  return (
    <div className={`so-card ${className}`}>
      {title && <h3 className="so-card-title">{icon}{title}</h3>}
      {children}
      {note && <p className="so-note">{note}</p>}
    </div>
  );
}

/** Leyenda propia: Chart.js apaga la suya para poder alinearla con las tablas y
 *  mantener el mismo swatch en las cuatro pestañas. */
function Legenda({ items }) {
  return (
    <div className="so-legend">
      {items.map((i) => (
        <span key={i.label} className="so-legend-item">
          <span className="so-swatch" style={{ background: i.color }} />{i.label}
        </span>
      ))}
    </div>
  );
}

const Empty = ({ children = 'Sin datos para el periodo y los filtros seleccionados.' }) =>
  <div className="so-empty">{children}</div>;

/** Aviso cuando el periodo equivalente del año anterior no existe completo en la
 *  serie. El backend ya anulo los deltas; esto explica el "—" para que no se lea
 *  como un fallo de carga. */
const AvisoSinComparativa = ({ d }) => (d?.comparable === false ? (
  <div className="so-warn">
    <FaExclamationTriangle />
    <span>
      Sin comparación año contra año: el periodo equivalente del año anterior no
      está completo en la serie. Los KPIs del periodo son correctos; las columnas
      «vs año ant.» aparecen como «—».
    </span>
  </div>
) : null);

// ═══════════════════════════════════════════════════════════════════════════
//  Pestaña: Resumen
// ═══════════════════════════════════════════════════════════════════════════
function ResumenTab({ d, ctx }) {
  const k = d.kpis || {};
  const dl = d.deltas || {};
  const serie = d.serie || [];
  const cadenas = d.cadenas || [];
  const marcas = d.marcas || [];

  // Valor del periodo y del mismo periodo del año anterior en el MISMO eje: son
  // la misma magnitud en pesos. Dos escalas para dos series de pesos seria una
  // mentira grafica (cualquier cruce entre las lineas seria un artefacto).
  const serieData = {
    labels: serie.map((s) => fmtYm(s.ym)),
    datasets: [
      {
        type: 'bar', label: 'Periodo actual', data: serie.map((s) => s.valor),
        backgroundColor: SERIES[0], borderRadius: 4, borderSkipped: false,
        maxBarThickness: 42, order: 2,
      },
      {
        type: 'line', label: 'Año anterior', data: serie.map((s) => s.valor_prev),
        borderColor: SERIES[1], backgroundColor: SERIES[1], borderWidth: 2,
        pointRadius: 3, pointHoverRadius: 5, tension: 0.3, spanGaps: true, order: 1,
      },
    ],
  };

  const dona = {
    labels: cadenas.map((c) => titulo(c.cliente)),
    datasets: [{
      data: cadenas.map((c) => c.valor),
      backgroundColor: cadenas.map((c) => colorOf(c.cliente, ctx.clientes)),
      // 2px del color de la superficie entre segmentos: sin el borde, dos
      // segmentos contiguos se leen como uno solo.
      borderColor: '#fff', borderWidth: 2,
    }],
  };

  const marcasData = {
    labels: marcas.map((m) => titulo(m.marca)),
    datasets: [{
      label: 'Valor', data: marcas.map((m) => m.valor),
      backgroundColor: marcas.map((m) => colorOf(m.marca, ctx.marcas)),
      borderRadius: 4, borderSkipped: false, maxBarThickness: 26,
    }],
  };

  const lider = cadenas[0];
  const mayorAlza = [...cadenas].filter((c) => c.yoy != null).sort((a, b) => b.yoy - a.yoy)[0];

  return (
    <>
      <AvisoSinComparativa d={d} />
      <div className="so-kpis">
        <Kpi label="Valor sell out" value={money(k.valor)} icon={<FaMoneyBillWave />}
          delta={dl.valor} color={SERIES[0]} hint={`${k.meses || 0} meses`} />
        <Kpi label="Unidades" value={num(k.unidades)} icon={<FaBoxes />}
          delta={dl.unidades} color={SERIES[1]} />
        <Kpi label="Precio promedio" value={money(k.precio_promedio)} icon={<FaTag />}
          delta={dl.precio_promedio} color={SERIES[2]} hint="por unidad" />
        <Kpi label="Tiendas con venta" value={num(k.tiendas)} icon={<FaStore />}
          delta={dl.tiendas} color={SERIES[3]} />
        <Kpi label="SKUs con venta" value={num(k.skus)} icon={<FaCubes />}
          delta={dl.skus} color={SERIES[4]} />
        <Kpi label="Venta por tienda" value={money(k.valor_por_tienda)} icon={<FaBullseye />}
          delta={dl.valor_por_tienda} color={SERIES[5]} hint="mensual" />
      </div>

      <div className="so-grid">
        <Card className="so-col-8" title="Evolución mensual vs año anterior" icon={<FaChartLine />}>
          <Legenda items={[
            { label: 'Periodo actual', color: SERIES[0] },
            { label: 'Mismo mes del año anterior', color: SERIES[1] },
          ]} />
          <div className="so-chart">
            {serie.length ? (
              <Bar data={serieData} options={{
                ...baseOpts(moneyC),
                plugins: {
                  ...baseOpts().plugins,
                  tooltip: {
                    ...baseOpts().plugins.tooltip,
                    callbacks: { label: (c) => `${c.dataset.label}: ${c.parsed.y == null ? '—' : money(c.parsed.y)}` },
                  },
                },
              }} />
            ) : <Empty />}
          </div>
        </Card>

        <Card className="so-col-4" title="Participación por cadena" icon={<FaLayerGroup />}>
          <div className="so-chart sm">
            {cadenas.length ? (
              <Doughnut data={dona} options={{
                responsive: true, maintainAspectRatio: false, cutout: '58%',
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    ...baseOpts().plugins.tooltip,
                    callbacks: { label: (c) => `${c.label}: ${money(c.parsed)} (${cadenas[c.dataIndex]?.share}%)` },
                  },
                },
              }} />
            ) : <Empty />}
          </div>
          <div className="so-rank">
            {cadenas.map((c) => (
              <div key={c.cliente} className="so-rank-row">
                <span className="so-rank-name">
                  <span className="so-swatch" style={{ background: colorOf(c.cliente, ctx.clientes) }} />
                  {titulo(c.cliente)}
                </span>
                <span className="so-rank-val">{c.share}%</span>
                <span className={`so-rank-yoy ${c.yoy == null ? '' : c.yoy >= 0 ? 'up' : 'down'}`}>
                  {c.yoy == null ? '—' : `${c.yoy >= 0 ? '+' : ''}${c.yoy}%`}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="so-col-6" title="Valor por marca" icon={<FaChartBar />}>
          {/* Alto proporcional al numero de barras: con alto fijo, la sexta marca
              queda fuera del recorte y el grafico dice que hay cinco. */}
          <div className="so-chart" style={{ height: Math.max(220, marcas.length * 42 + 56) }}>
            {marcas.length ? (
              <Bar data={marcasData} options={{
                ...baseOpts(moneyC),
                indexAxis: 'y',
                scales: {
                  y: { grid: { display: false }, ticks: { color: AXIS, font: { size: 11 } } },
                  x: { beginAtZero: true, grid: { color: GRID, drawBorder: false }, ticks: { color: AXIS, font: { size: 11 }, callback: moneyC } },
                },
                plugins: {
                  ...baseOpts().plugins,
                  tooltip: {
                    ...baseOpts().plugins.tooltip,
                    callbacks: { label: (c) => `${money(c.parsed.x)} · ${marcas[c.dataIndex]?.share}% del total` },
                  },
                },
              }} />
            ) : <Empty />}
          </div>
        </Card>

        <Card className="so-col-6" title="Marcas: valor, mezcla y crecimiento" icon={<FaTag />}>
          <div className="so-table-wrap">
            <table className="so-table">
              <thead>
                <tr>
                  <th>Marca</th><th className="r">Valor</th><th className="r">Mezcla</th>
                  <th className="r">Unidades</th><th className="r">Tiendas</th><th className="r">vs año ant.</th>
                </tr>
              </thead>
              <tbody>
                {marcas.map((m) => (
                  <tr key={m.marca}>
                    <td className="strong">
                      <span className="so-swatch" style={{ background: colorOf(m.marca, ctx.marcas) }} />
                      {titulo(m.marca)}
                    </td>
                    <td className="r">{money(m.valor)}</td>
                    <td className="r">{m.share}%</td>
                    <td className="r">{num(m.unidades)}</td>
                    <td className="r">{num(m.tiendas)}</td>
                    <td className={`r ${m.yoy == null ? '' : m.yoy >= 0 ? 'up' : 'down'}`}>
                      {m.yoy == null ? '—' : `${m.yoy >= 0 ? '+' : ''}${m.yoy}%`}
                    </td>
                  </tr>
                ))}
                {!marcas.length && <tr><td colSpan={6}><Empty /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {lider && (
        <div className="so-insight">
          <FaInfoCircle />
          <span>
            <strong>{titulo(lider.cliente)}</strong> concentra el {lider.share}% del valor
            {lider.yoy != null && <> ({lider.yoy >= 0 ? '+' : ''}{lider.yoy}% vs. año anterior)</>}
            {mayorAlza && mayorAlza.cliente !== lider.cliente && (
              <> · el mayor crecimiento es <strong>{titulo(mayorAlza.cliente)}</strong> con {mayorAlza.yoy >= 0 ? '+' : ''}{mayorAlza.yoy}%</>
            )}.
          </span>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Pestaña: Cadenas
// ═══════════════════════════════════════════════════════════════════════════
function CadenasTab({ d, ctx }) {
  const cadenas = d.cadenas || [];
  const meses = d.meses || [];
  const series = d.series || [];

  const lineas = {
    labels: meses.map(fmtYm),
    datasets: series.map((s) => ({
      label: titulo(s.cliente), data: s.valores,
      borderColor: colorOf(s.cliente, ctx.clientes),
      backgroundColor: colorOf(s.cliente, ctx.clientes),
      borderWidth: 2, pointRadius: 2.5, pointHoverRadius: 5, tension: 0.3,
    })),
  };

  const prod = {
    labels: cadenas.map((c) => titulo(c.cliente)),
    datasets: [{
      label: 'Venta por tienda al mes', data: cadenas.map((c) => c.valor_por_tienda),
      backgroundColor: cadenas.map((c) => colorOf(c.cliente, ctx.clientes)),
      borderRadius: 4, borderSkipped: false, maxBarThickness: 44,
    }],
  };

  const top = cadenas[0];
  const masProductiva = [...cadenas].sort((a, b) => b.valor_por_tienda - a.valor_por_tienda)[0];

  return (
    <>
      <AvisoSinComparativa d={d} />
      <div className="so-grid">
        <Card className="so-col-7" title="Evolución mensual por cadena" icon={<FaChartLine />}>
          <Legenda items={series.map((s) => ({ label: titulo(s.cliente), color: colorOf(s.cliente, ctx.clientes) }))} />
          <div className="so-chart">
            {series.length ? (
              <Line data={lineas} options={{
                ...baseOpts(moneyC),
                plugins: {
                  ...baseOpts().plugins,
                  tooltip: { ...baseOpts().plugins.tooltip, callbacks: { label: (c) => `${c.dataset.label}: ${money(c.parsed.y)}` } },
                },
              }} />
            ) : <Empty />}
          </div>
        </Card>

        <Card className="so-col-5" title="Venta por tienda al mes" icon={<FaBullseye />}
          note="Productividad de cada punto de venta: valor del periodo dividido entre tiendas con venta y meses. Es lo que separa a una cadena con pocas tiendas muy productivas de otra con muchas tiendas de baja rotación.">
          <div className="so-chart">
            {cadenas.length ? (
              <Bar data={prod} options={{
                ...baseOpts(moneyC),
                plugins: {
                  ...baseOpts().plugins,
                  tooltip: {
                    ...baseOpts().plugins.tooltip,
                    callbacks: {
                      label: (c) => `${money(c.parsed.y)} por tienda/mes`,
                      afterLabel: (c) => `${num(cadenas[c.dataIndex]?.tiendas)} tiendas`,
                    },
                  },
                },
              }} />
            ) : <Empty />}
          </div>
        </Card>

        <Card className="so-col-12" title="Detalle por cadena" icon={<FaStore />}
          note="Sin rotación = líneas producto-tienda-mes reportadas en cero. No es comparable entre cadenas: cada una decide si manda o no las líneas de producto listado sin venta, por eso se muestra junto al conteo. Solo llega en el feed desde marzo de 2026.">
          <div className="so-table-wrap">
            <table className="so-table">
              <thead>
                <tr>
                  <th>Cadena</th>
                  <th className="r">Valor</th><th className="r">Mezcla</th><th className="r">vs año ant.</th>
                  <th className="r">Unidades</th><th className="r">Ud. vs año ant.</th>
                  <th className="r">Tiendas</th><th className="r">Venta/tienda/mes</th>
                  <th className="r">SKUs/tienda</th><th className="r">Precio prom.</th>
                  <th className="r">Sin rotación</th>
                </tr>
              </thead>
              <tbody>
                {cadenas.map((c) => (
                  <tr key={c.cliente}>
                    <td className="strong">
                      <span className="so-swatch" style={{ background: colorOf(c.cliente, ctx.clientes) }} />
                      {titulo(c.cliente)}
                    </td>
                    <td className="r">{money(c.valor)}</td>
                    <td className="r">{c.share}%</td>
                    <td className={`r ${c.yoy == null ? '' : c.yoy >= 0 ? 'up' : 'down'}`}>
                      {c.yoy == null ? '—' : `${c.yoy >= 0 ? '+' : ''}${c.yoy}%`}
                    </td>
                    <td className="r">{num(c.unidades)}</td>
                    <td className={`r ${c.yoy_unidades == null ? '' : c.yoy_unidades >= 0 ? 'up' : 'down'}`}>
                      {c.yoy_unidades == null ? '—' : `${c.yoy_unidades >= 0 ? '+' : ''}${c.yoy_unidades}%`}
                    </td>
                    <td className="r">
                      {num(c.tiendas)}
                      {c.tiendas_prev != null && (
                        <span className="so-sub"> ({c.tiendas - c.tiendas_prev >= 0 ? '+' : ''}{c.tiendas - c.tiendas_prev})</span>
                      )}
                    </td>
                    <td className="r strong">{money(c.valor_por_tienda)}</td>
                    <td className="r">{c.skus_por_tienda}</td>
                    <td className="r">{money(c.precio_promedio)}</td>
                    <td className="r">
                      {c.sin_rotacion == null ? '—' : `${c.sin_rotacion}%`}
                      {c.lineas_total ? <span className="so-sub"> {num(c.lineas_cero)}/{num(c.lineas_total)}</span> : null}
                    </td>
                  </tr>
                ))}
                {!cadenas.length && <tr><td colSpan={11}><Empty /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {top && masProductiva && (
        <div className="so-insight">
          <FaInfoCircle />
          <span>
            <strong>{titulo(masProductiva.cliente)}</strong> es la cadena más productiva con{' '}
            {money(masProductiva.valor_por_tienda)} por tienda al mes en {num(masProductiva.tiendas)} tiendas
            {top.cliente !== masProductiva.cliente && (
              <> , frente a {money(top.valor_por_tienda)} de <strong>{titulo(top.cliente)}</strong>, que lidera en valor total con {num(top.tiendas)} tiendas</>
            )}.
          </span>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Pestaña: Producto
// ═══════════════════════════════════════════════════════════════════════════
function ProductosTab({ d, ctx }) {
  const prods = d.productos || [];
  const par = d.pareto || {};   // el corte por marca (d.marcas) se pinta en Resumen
  const universo = d.universo_tiendas || 0;

  const curva = {
    labels: (par.curva || []).map((c) => c.n),
    datasets: [{
      label: '% acumulado del valor', data: (par.curva || []).map((c) => c.acumulado),
      borderColor: SERIES[0], backgroundColor: 'rgba(42,120,214,.10)',
      borderWidth: 2, pointRadius: 0, pointHoverRadius: 4, tension: 0.25, fill: true,
    }],
  };

  // Brecha de distribucion: el hueco entre el SKU y el universo de tiendas que YA
  // compra la categoria. Es la palanca de crecimiento que no depende de vender
  // mas por tienda, sino de estar en mas tiendas.
  const brecha = [...prods]
    .filter((p) => p.distribucion < 85)
    .sort((a, b) => (b.valor / Math.max(b.distribucion, 1)) - (a.valor / Math.max(a.distribucion, 1)))
    .slice(0, 8);

  return (
    <>
      <AvisoSinComparativa d={d} />
      <div className="so-tiles">
        <div className="so-tile" style={{ '--accent': SERIES[0] }}>
          <div className="so-tile-value">{par.skus_80 || 0} de {par.total_skus || 0}</div>
          <div className="so-tile-label">SKUs generan el 80% del valor ({par.pct_skus_80 || 0}% del catálogo)</div>
        </div>
        <div className="so-tile" style={{ '--accent': SERIES[1] }}>
          <div className="so-tile-value">{num(universo)}</div>
          <div className="so-tile-label">Tiendas con venta de la categoría (universo de distribución)</div>
        </div>
        <div className="so-tile" style={{ '--accent': SERIES[2] }}>
          <div className="so-tile-value">{prods[0] ? `${prods[0].distribucion}%` : '—'}</div>
          <div className="so-tile-label">Distribución del SKU líder{prods[0] ? ` · ${titulo(prods[0].producto)}` : ''}</div>
        </div>
      </div>

      <div className="so-grid">
        <Card className="so-col-5" title="Concentración del catálogo" icon={<FaChartLine />}
          note={`Los SKUs ordenados de mayor a menor venta. ${par.skus_80 || 0} de ${par.total_skus || 0} productos acumulan el 80% del valor.`}>
          <div className="so-chart">
            {(par.curva || []).length ? (
              <Line data={curva} options={{
                ...baseOpts((v) => `${v}%`),
                scales: {
                  x: { grid: { display: false }, ticks: { color: AXIS, font: { size: 11 }, maxTicksLimit: 10 }, title: { display: true, text: 'SKUs acumulados', color: AXIS, font: { size: 11 } } },
                  y: { beginAtZero: true, max: 100, grid: { color: GRID, drawBorder: false }, ticks: { color: AXIS, font: { size: 11 }, callback: (v) => `${v}%` } },
                },
                plugins: {
                  ...baseOpts().plugins,
                  tooltip: { ...baseOpts().plugins.tooltip, callbacks: { title: (c) => `Top ${c[0].label} SKUs`, label: (c) => `${c.parsed.y}% del valor` } },
                },
              }} />
            ) : <Empty />}
          </div>
        </Card>

        <Card className="so-col-7" title="Mayor brecha de distribución" icon={<FaBullseye />}
          note="SKUs con venta relevante que aún no llegan al 85% del universo de tiendas. La barra mide en qué porcentaje de las tiendas con venta de la categoría se vendió el producto.">
          {brecha.length ? (
            <div className="so-rank">
              {brecha.map((p) => (
                <div key={p.sku} className="so-rank-row wide">
                  <span className="so-rank-name" title={titulo(p.producto)}>
                    <span className="so-swatch" style={{ background: colorOf(p.marca, ctx.marcas) }} />
                    {titulo(p.producto)}
                  </span>
                  <span className="so-rank-track">
                    <span className="so-rank-fill" style={{ width: `${p.distribucion}%`, background: colorOf(p.marca, ctx.marcas) }} />
                  </span>
                  <span className="so-rank-val">{p.distribucion}%</span>
                  <span className="so-rank-extra">{moneyC(p.valor)}</span>
                </div>
              ))}
            </div>
          ) : <Empty>Todos los SKUs del periodo superan el 85% de distribución.</Empty>}
        </Card>

        <Card className="so-col-12" title="Ranking de productos" icon={<FaTag />}
          note="Distribución = % de las tiendas con venta de la categoría donde se vendió el SKU. Rotación = unidades por tienda y mes, contando solo las tiendas donde sí se vende: separa el producto que vende poco por estar en pocas tiendas del que está en todas y no rota.">
          <div className="so-table-wrap">
            <table className="so-table">
              <thead>
                <tr>
                  <th>#</th><th>Producto</th><th>Marca</th>
                  <th className="r">Valor</th><th className="r">Mezcla</th><th className="r">vs año ant.</th>
                  <th className="r">Unidades</th><th className="r">Precio prom.</th>
                  <th className="r">Tiendas</th><th className="r">Distribución</th><th className="r">Rotación</th>
                </tr>
              </thead>
              <tbody>
                {prods.map((p, i) => (
                  <tr key={p.sku}>
                    <td className="so-idx">{i + 1}</td>
                    <td className="strong">
                      {titulo(p.producto)}
                      <div className="so-sub">{p.sku}{p.pareto ? ` · Pareto ${p.pareto}` : ''}</div>
                    </td>
                    <td>
                      <span className="so-swatch" style={{ background: colorOf(p.marca, ctx.marcas) }} />
                      {titulo(p.marca)}
                    </td>
                    <td className="r">{money(p.valor)}</td>
                    <td className="r">{p.share}%</td>
                    <td className={`r ${p.yoy == null ? '' : p.yoy >= 0 ? 'up' : 'down'}`}>
                      {p.yoy == null ? '—' : `${p.yoy >= 0 ? '+' : ''}${p.yoy}%`}
                    </td>
                    <td className="r">{num(p.unidades)}</td>
                    <td className="r">{money(p.precio_promedio)}</td>
                    <td className="r">{num(p.tiendas)}</td>
                    <td className="r">{p.distribucion}%</td>
                    <td className="r">{p.rotacion}</td>
                  </tr>
                ))}
                {!prods.length && <tr><td colSpan={11}><Empty /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Pestaña: Tiendas
// ═══════════════════════════════════════════════════════════════════════════
function TiendasTab({ d }) {
  const top = d.top || [];
  const c = d.concentracion || {};

  const barras = {
    labels: top.map((t) => titulo(t.sucursal)),
    datasets: [{
      label: 'Valor', data: top.map((t) => t.valor),
      backgroundColor: SERIES[0], borderRadius: 4, borderSkipped: false, maxBarThickness: 22,
    }],
  };

  return (
    <>
      <AvisoSinComparativa d={d} />
      <div className="so-tiles">
        <div className="so-tile" style={{ '--accent': SERIES[0] }}>
          <div className="so-tile-value">{num(c.tiendas)}</div>
          <div className="so-tile-label">Tiendas con venta en el periodo</div>
        </div>
        <div className="so-tile" style={{ '--accent': SERIES[1] }}>
          <div className="so-tile-value">{num(c.tiendas_50)} <span className="so-tile-pct">({c.pct_50}%)</span></div>
          <div className="so-tile-label">Tiendas que generan la mitad del valor</div>
        </div>
        <div className="so-tile" style={{ '--accent': SERIES[2] }}>
          <div className="so-tile-value">{num(c.tiendas_80)} <span className="so-tile-pct">({c.pct_80}%)</span></div>
          <div className="so-tile-label">Tiendas que generan el 80% del valor</div>
        </div>
        <div className="so-tile" style={{ '--accent': SERIES[3] }}>
          <div className="so-tile-value">{money(c.mediana)}</div>
          <div className="so-tile-label">Venta de la tienda mediana · promedio {money(c.promedio)}</div>
        </div>
      </div>

      <div className="so-grid">
        <Card className="so-col-5" title="Top tiendas por valor" icon={<FaChartBar />}>
          {/* Igual que en marcas: el alto lo fija el numero de tiendas del top. */}
          <div className="so-chart" style={{ height: Math.max(260, top.length * 30 + 56) }}>
            {top.length ? (
              <Bar data={barras} options={{
                ...baseOpts(moneyC),
                indexAxis: 'y',
                scales: {
                  y: { grid: { display: false }, ticks: { color: AXIS, font: { size: 10 } } },
                  x: { beginAtZero: true, grid: { color: GRID, drawBorder: false }, ticks: { color: AXIS, font: { size: 11 }, callback: moneyC } },
                },
                plugins: {
                  ...baseOpts().plugins,
                  tooltip: {
                    ...baseOpts().plugins.tooltip,
                    callbacks: {
                      title: (t) => titulo(top[t[0].dataIndex]?.tienda || ''),
                      label: (t) => money(t.parsed.x),
                    },
                  },
                },
              }} />
            ) : <Empty />}
          </div>
        </Card>

        <Card className="so-col-7" title="Detalle de las tiendas líderes" icon={<FaTrophy />}
          note={`La mediana (${money(c.mediana)}) está muy por debajo del promedio (${money(c.promedio)}): la venta se concentra en pocas tiendas grandes y la tienda típica vende bastante menos de lo que sugiere el promedio.`}>
          <div className="so-table-wrap">
            <table className="so-table">
              <thead>
                <tr>
                  <th>#</th><th>Tienda</th><th>Cadena</th>
                  <th className="r">Valor</th><th className="r">Mensual</th>
                  <th className="r">vs año ant.</th><th className="r">Unidades</th>
                  <th className="r">SKUs</th><th className="r">Acumulado</th>
                </tr>
              </thead>
              <tbody>
                {top.map((t) => (
                  <tr key={t.tienda}>
                    <td className="so-idx">{t.rank}</td>
                    <td className="strong">{titulo(t.sucursal)}</td>
                    <td>{titulo(t.cliente)}</td>
                    <td className="r">{money(t.valor)}</td>
                    <td className="r">{money(t.valor_mes)}</td>
                    <td className={`r ${t.yoy == null ? '' : t.yoy >= 0 ? 'up' : 'down'}`}>
                      {t.yoy == null ? '—' : `${t.yoy >= 0 ? '+' : ''}${t.yoy}%`}
                    </td>
                    <td className="r">{num(t.unidades)}</td>
                    <td className="r">{num(t.skus)}</td>
                    <td className="r">{t.acumulado}%</td>
                  </tr>
                ))}
                {!top.length && <tr><td colSpan={9}><Empty /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div className="so-insight">
        <FaInfoCircle />
        <span>
          El {c.pct_50}% de las tiendas ({num(c.tiendas_50)} de {num(c.tiendas)}) genera la mitad del valor
          y el {c.pct_80}% genera el 80%.
        </span>
      </div>
    </>
  );
}
